import * as _ from 'lodash';
import DropZone from 'react-dropzone';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { archiveUploadAccept, validArchiveUploadExtensions } from '../../../utils';
import Button, { BUTTON_TYPE } from '../../generic/Button';
import { LabeledTextArea, LabeledTextInput, TextInput } from '../../generic/Forms';
import InfoPanel from '../../generic/InfoPanel';

import { ModalAddDependencyAlert } from './ModalAddDependencyAlert';
import type { ArchiveModalData, UploadModalData, URLModalData } from './types';

type AddUploadDependencyProps = {
  onChange: (dep: UploadModalData) => void;
  dependencyToAdd: UploadModalData;
  isDisabled: boolean;
};

const AddUploadDependency = ({ isDisabled, onChange, dependencyToAdd }: AddUploadDependencyProps) => {
  const onAttributeChange = (
    attr: 'name' | 'version' | 'homepage' | 'description' | 'file' | 'url' | 'vendoredPath',
    value: string | File | undefined,
  ) => {
    const newDep: Partial<UploadModalData> = {
      ...dependencyToAdd,
      [attr]: value,
    };

    // set `kind` and clear out `url` field if user selects an archive
    if (attr === 'file') {
      (newDep as URLModalData).url = undefined;
      newDep.kind = 'archive';
    }

    // set `kind` and clear out `file` field if user inputs a url
    if (attr === 'url') {
      (newDep as ArchiveModalData).file = undefined;
      newDep.kind = 'url';
    }

    onChange(newDep);
  };

  const onDrop = (acceptedFiles: File[]) => {
    const file = _.head(acceptedFiles);
    if (!file) {
      return;
    }

    onAttributeChange('file', file);
  };

  // this feature check should not be needed; but just in case someting slips through
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const isVendored = isVendoredDependenciesEnabled && (dependencyToAdd.vendoredPath !== undefined);
  const archiveFileToAdd = dependencyToAdd.kind === 'archive' && dependencyToAdd.file;

  return (
    <div className="upload-panel">
      <p className="upload-panel__title">
        Your archive source code locally or from the web
      </p>
      <ModalAddDependencyAlert dependencyToAdd={dependencyToAdd} />
      <div className="upload-panel__body">
        {archiveFileToAdd ?
          <div className="upload-panel__body__section">
            <LabeledTextInput
              type="text"
              label="Archive"
              value={archiveFileToAdd.name}
              suffix={
                <Button
                  type={BUTTON_TYPE.SECONDARY}
                  isDisabled={isDisabled}
                  onClick={() => onAttributeChange('file', undefined)}>
                  Remove
                </Button>
              }
              isDisabled
            />
          </div> :
          <div className="upload-panel__body__file-container upload-panel__body__section">
            <div>
              <p>Local archive</p>
              <DropZone
                onDrop={onDrop}
                accept={archiveUploadAccept}
                disabled={isDisabled}
                >
                {
                  ({ getRootProps, getInputProps }) =>
                    <div className="upload-panel__body__file-container__archive-dropzone clickable" {...getRootProps()}>
                      <p>Drag and drop or click to upload</p>
                      <input {...getInputProps()} />
                    </div>
                }
              </DropZone>
            </div>
            <div className="upload-panel__body__file-container__or-separator"> OR</div>
            <div className="upload-panel__body__file-container__url">
              <p>URL-based archive</p>
              <TextInput
                placeholder="https://dependency.com/code.zip"
                label="url-to-upload"
                value={(dependencyToAdd as URLModalData).url || ''}
                isDisabled={isDisabled}
                onChange={url => onAttributeChange('url', url)}
              />
            </div>
            <p className="upload-panel__body__file-container__description">
              Supported extensions: {validArchiveUploadExtensions.join(', ')}
            </p>
          </div>}
        {isVendored && (
          <div className="locator-panel__body__section locator-panel__body__name-version">
            <LabeledTextInput
              type="text"
              label="Path to Vendored Dependency"
              value={dependencyToAdd.vendoredPath}
              placeholder="/"
              isRequired
              isDisabled={isDisabled}
              onChange={n => onAttributeChange('vendoredPath', n)}
            />
          </div>
        )}
        <div className="upload-panel__body__section locator-panel__body__name-version">
          <LabeledTextInput
            type="text"
            label="Name"
            value={dependencyToAdd.name || ''}
            placeholder="Dependency"
            isRequired
            isDisabled={isDisabled}
            onChange={n => onAttributeChange('name', n)}
          />
          <LabeledTextInput
            type="text"
            label="Version"
            value={dependencyToAdd.version || ''}
            placeholder="1.2.3"
            isRequired
            isDisabled={isDisabled}
            onChange={v => onAttributeChange('version', v)}
          />
        </div>
        <div className="upload-panel__body__section">
          <LabeledTextInput
            type="text"
            label="Homepage"
            value={dependencyToAdd.homepage || ''}
            placeholder="www.dependency.com"
            isDisabled={isDisabled}
            onChange={hp => onAttributeChange('homepage', hp)}
          />
        </div>
        <div className="upload-panel__body__section">
          <LabeledTextArea
            label="Description"
            value={dependencyToAdd.description || ''}
            isDisabled={isDisabled}
            onChange={(d: string) => onAttributeChange('description', d)}
          />
        </div>
      </div>
      <div className="upload-panel__footer">
        <InfoPanel>
          Uploading an archive can take some time depending on its size and your upload speed.
        </InfoPanel>
      </div>
    </div>
  );
};

export default AddUploadDependency;
